/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import { IModelConnection, ScreenViewport } from "@itwin/core-frontend"; //!!OA
import { GeometricModelState } from "@itwin/core-frontend";
import { ModelProps, ModelQueryParams } from "@itwin/core-common";
import { GeometricElement3dProps, ElementProps, Placement3d } from "@itwin/core-common";
import { Point3d, Range3d } from "@itwin/core-geometry";
import { Id64 } from "@itwin/core-bentley";

import { cTime } from "./time";

export interface ItemTypeProps {
  name: string;
  type: string;
  skid: string;
}

export class cQuery {
  private static iModel: IModelConnection;
  private static sViewPort: ScreenViewport;

  public static initQuery = async (vp: ScreenViewport, imodel: IModelConnection) => {
    this.iModel = imodel;
    this.sViewPort = vp;
  }

  public static fetchModelProps = async (modelName : string) => {
    const modelProps : ModelProps[] = [];

    const modelQueryParams: ModelQueryParams = {
      from: GeometricModelState.classFullName,  //from: SpatialModelState.classFullName,
      wantPrivate: false,
    };

    const curModelProps = await this.iModel.models.queryProps(modelQueryParams);

    for (const mProps of curModelProps) {
//      if (mProps.id && mProps.classFullName === "BisCore:PhysicalModel") {
      if (mProps.id) {
        if (modelName === undefined || mProps.name?.includes(modelName)) {
          modelProps.push(mProps);
        }
      }
    }  

    return modelProps;
  }

  public static getElementRange = async (elementId: string): Promise<Range3d | undefined> => {
    if (!Id64.isValid(elementId)) return;

    // from frontend-sample-showcase>clash-review-sample>ClashReviewApp.tsx => calcClashCenter{}
    let range = Range3d.createNull();
    const elemProps = (await this.iModel.elements.getProps([
      elementId,
    ])) as GeometricElement3dProps[];

    if (elemProps.length !== 0) {
      elemProps.forEach((prop: GeometricElement3dProps) => {
        const placement = Placement3d.fromJSON(prop.placement); //!!OA fromJSON??? there has to be a better way
        range = placement.calculateRange();
      });
    }
    return Promise.resolve(range);
  }

  public static getElementId = async (elementTag: string, match?: boolean, type?: number): Promise<string[] | undefined> => {
    const elementIds: string[] = [];

    if (!elementTag) return elementIds;

    const tag = elementTag.toUpperCase();

    let search: string;
    if (match === false) {
      search = `'%${tag}%'`;
    } else {
      search = `'${tag}'`;
    }

    var className: string;
    switch (type) {
      case 2:
        className = "Biscore.GeometricElement2d";
        break;
      case 3:
        className = "Biscore.SpatialElement"; //"Biscore.GeometricElement3d";
        break;
      default:
        className = "Biscore.SpatialElement";
    }

    const query = `SELECT ECInstanceId AS ecId 
                   FROM ${className} 
                   WHERE UserLabel LIKE ${search}`;
    const rows = this.iModel.query(query);
    for await (const row of rows) {
      elementIds.push(row[0]);
   }

    return elementIds!;
  }

  public static getElementIdFromTag = async (elementTag: string, match?: boolean): Promise<string[] | undefined> => {
    return this.getElementId(elementTag, match, 3);
  }

  public static get2dElementIdFromTag = async (elementTag: string,  match?: boolean): Promise<string[] | undefined> => {
    return this.getElementId(elementTag, match, 2);
  }

  public static getElementTag = async (eleIds: string[]): Promise<string[]> => {
    const tags: string[] = [];

    if (Id64.isValid(eleIds[0])) {
      const elemProps = (await this.iModel.elements.getProps(eleIds)) as ElementProps[];
      for (const prop of elemProps) {
        tags.push(prop.userLabel!);
      }
    }

    return tags;
  }

  public static getItemTypeByTag = async (itemTypeName: string, keyword: string, match?: boolean): Promise<ItemTypeProps[] | undefined> => {
    const itemTypeProps: ItemTypeProps[] = [];
    const tag = keyword.toUpperCase();

    let search: string;
    search = tag;
    if (search === "*") search = "";
    if (match === false) {
      search = `'%${search}%'`;
    } else {
      search = `'${search}'`;
    }

    const query = `SELECT name, type, skid 
                   FROM DgnCustomItemTypes_${itemTypeName} 
                   WHERE name LIKE ${search} OR type LIKE ${search} OR skid LIKE ${search}`;
    const rows = this.iModel.query(query);
    for await (const row of rows) {
      itemTypeProps.push(row);
    }

    return itemTypeProps!;
  }

  public static getItemTypeById = async (itemTypeName: string, elementId: string): Promise<ItemTypeProps[] | undefined> => {
    const itemTypeProps: ItemTypeProps[] = [];

    const query = `SELECT name, type, skid 
                   FROM DgnCustomItemTypes_${itemTypeName} 
                   WHERE ECInstanceId=${elementId}`;
    const rows = this.iModel.query(query);
    for await (const row of rows) {
      itemTypeProps.push(row);
    }

    return itemTypeProps!;
  }
}
